import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react"
import type { Session } from "@supabase/supabase-js"
import {
  ArrowCounterClockwise,
  ArrowDownLeft,
  ArrowUpRight,
  CaretDown,
  ChartLineUp,
  CheckCircle,
  CurrencyCircleDollar,
  DownloadSimple,
  FileArrowUp,
  GearSix,
  House,
  Lightning,
  ListBullets,
  LockKey,
  Plus,
  ShareNetwork,
  ShieldCheck,
  SignOut,
  SpinnerGap,
  UsersThree,
  WarningCircle,
  X,
  XCircle,
} from "@phosphor-icons/react"
import { demoLedger } from "./lib/demo"
import { callLedgerRpc, downloadSnapshot, isConfigured, loadLedger, myWorkspaceId, supabase } from "./lib/supabase"
import { formatAmount, signedAmount } from "./lib/amount"
import type { Balance, Bet, LedgerData, Partner, Unit, Workspace } from "./types"

type View = "dashboard" | "host" | "split" | "pending" | "adjust" | "tabs" | "history" | "partners" | "settings" | "admin"

type AdminUser = {
  id: string
  email: string
  display_name: string | null
  role: "admin" | "member"
  disabled_at: string | null
  workspaces: Workspace[] | Workspace | null
}

type AdminCredentials = {
  email: string
  password: string
}

type AdminControl = {
  users: AdminUser[]
  loading: boolean
  switchWorkspace: (workspaceId: string) => void
  inviteUser: (email: string, name: string) => Promise<AdminCredentials>
  disableUser: (userId: string) => Promise<void>
  resetCredentials: (userId: string) => Promise<AdminCredentials>
}

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

type Toast = { kind: "success" | "error"; message: string }
type RunAction = (action: () => Promise<unknown>, success: string) => Promise<boolean>

const nav: Array<{ view: View; label: string; icon: typeof House }> = [
  { view: "dashboard", label: "Dashboard", icon: House },
  { view: "host", label: "Host", icon: ArrowUpRight },
  { view: "split", label: "Split In", icon: ArrowDownLeft },
  { view: "pending", label: "Pending", icon: Lightning },
  { view: "adjust", label: "Payments", icon: CheckCircle },
  { view: "tabs", label: "Tabs", icon: CurrencyCircleDollar },
  { view: "history", label: "History", icon: ListBullets },
  { view: "partners", label: "Partners", icon: UsersThree },
  { view: "settings", label: "Settings", icon: GearSix },
]

export default function App() {
  const content = !isConfigured
    ? <LedgerShell data={demoLedger} isDemo ownWorkspaceId={demoLedger.workspace.id} reload={async () => undefined} />
    : <AuthenticatedApp />
  return <><InstallPrompt />{content}</>
}

function InstallPrompt() {
  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null)
  const [show, setShow] = useState(false)
  const [isIos, setIsIos] = useState(false)

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
    if (standalone) return

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    const dismissed = window.localStorage.getItem("action-tracker-install-dismissed") === "1"
    setIsIos(ios)

    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault()
      setInstallEvent(event as InstallPromptEvent)
      if (!dismissed) setShow(true)
    }

    function onAppInstalled() {
      setInstallEvent(null)
      setShow(false)
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt)
    window.addEventListener("appinstalled", onAppInstalled)
    if (ios && !dismissed) setShow(true)
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt)
      window.removeEventListener("appinstalled", onAppInstalled)
    }
  }, [])

  async function install() {
    if (!installEvent) return
    await installEvent.prompt()
    await installEvent.userChoice
    setInstallEvent(null)
    setShow(false)
  }

  function dismiss() {
    window.localStorage.setItem("action-tracker-install-dismissed", "1")
    setShow(false)
  }

  if (!show) return null
  return (
    <aside className="install-prompt" aria-label="Install Action Tracker" aria-live="polite">
      <div className="install-prompt-copy">
        <span className="install-prompt-icon"><DownloadSimple size={18} /></span>
        <div>
          <strong>Install Action Tracker</strong>
          <p>{isIos ? <>In Safari, tap <ShareNetwork size={15} weight="bold" /> Share, then <b>Add to Home Screen</b>.</> : "Keep your ledger one tap away with the installed app."}</p>
        </div>
      </div>
      <div className="install-prompt-actions">
        {installEvent && <button className="primary-button compact install-button" onClick={() => void install()}>Install</button>}
        <button className="icon-button" onClick={dismiss} aria-label="Dismiss install prompt"><X size={18} /></button>
      </div>
    </aside>
  )
}

function AuthenticatedApp() {
  const [session, setSession] = useState<Session | null | undefined>(undefined)

  useEffect(() => {
    const client = supabase
    if (!client) return
    client.auth.getSession().then(({ data }) => setSession(data.session))
    const { data: listener } = client.auth.onAuthStateChange((_event, nextSession) => setSession(nextSession))
    return () => listener.subscription.unsubscribe()
  }, [])

  if (session === undefined) return <LoadingScreen label="Opening your ledger" fullScreen />
  if (!session) return <LoginScreen />
  return <LiveLedger session={session} />
}

function LoginScreen() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const client = supabase
    if (!client) return
    setBusy(true)
    setError("")
    const { error: signInError } = await client.auth.signInWithPassword({ email, password })
    setBusy(false)
    if (signInError) setError("We could not sign you in. Check your email and password, then try again.")
  }

  return (
    <main className="auth-page">
      <section className="auth-panel">
        <div className="mark"><img src="/icons/icon-192.png" alt="" /></div>
        <h1>Action Tracker</h1>
        <p className="auth-intro">A private ledger for actions, partners, and clean settlements.</p>
        <form onSubmit={submit} className="stack">
          <label>Email<input autoComplete="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label>Password<input autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
          {error && <p className="form-error"><WarningCircle size={18} />{error}</p>}
          <button className="primary-button" disabled={busy}>{busy ? <SpinnerGap className="spin" size={18} /> : <LockKey size={18} />}Sign in</button>
        </form>
        <p className="auth-note">There is no public registration. Ask your administrator for your email and password.</p>
      </section>
    </main>
  )
}

function LiveLedger({ session }: { session: Session }) {
  const [ownWorkspaceId, setOwnWorkspaceId] = useState("")
  const [activeWorkspaceId, setActiveWorkspaceId] = useState("")
  const [data, setData] = useState<LedgerData | null>(null)
  const [error, setError] = useState("")
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [adminLoading, setAdminLoading] = useState(false)
  const [realtimeStatus, setRealtimeStatus] = useState<"connecting" | "live" | "fallback">("connecting")
  const reloadTimer = useRef<number | null>(null)
  const reloadInFlight = useRef(false)
  const reloadQueued = useRef(false)

  useEffect(() => {
    myWorkspaceId()
      .then((workspaceId) => {
        setOwnWorkspaceId(workspaceId)
        setActiveWorkspaceId(workspaceId)
      })
      .catch((reason: Error) => setError(reason.message))
  }, [])

  async function reload() {
    if (!activeWorkspaceId) return
    if (reloadInFlight.current) {
      reloadQueued.current = true
      return
    }
    reloadInFlight.current = true
    setError("")
    try {
      setData(await loadLedger(activeWorkspaceId))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Your ledger could not be loaded.")
    } finally {
      reloadInFlight.current = false
      if (reloadQueued.current) {
        reloadQueued.current = false
        window.setTimeout(() => void reload(), 0)
      }
    }
  }

  useEffect(() => {
    void reload()
  }, [activeWorkspaceId])

  useEffect(() => {
    const client = supabase
    if (!client || !activeWorkspaceId) return
    setRealtimeStatus("connecting")
    const refresh = () => {
      if (reloadTimer.current !== null) return
      reloadTimer.current = window.setTimeout(() => {
        reloadTimer.current = null
        void reload()
      }, 350)
    }
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refresh()
    }
    const pollingTimer = window.setInterval(refreshWhenVisible, 30_000)
    document.addEventListener("visibilitychange", refreshWhenVisible)
    const channel = client
      .channel("ledger-workspace:" + activeWorkspaceId)
      .on("postgres_changes", { event: "*", schema: "public", table: "workspaces", filter: "id=eq." + activeWorkspaceId }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "settlement_units", filter: "workspace_id=eq." + activeWorkspaceId }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "partners", filter: "workspace_id=eq." + activeWorkspaceId }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "bets", filter: "workspace_id=eq." + activeWorkspaceId }, refresh)
      // Allocations inherit workspace scope through bets and do not have workspace_id.
      .on("postgres_changes", { event: "*", schema: "public", table: "bet_allocations" }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "adjustments", filter: "workspace_id=eq." + activeWorkspaceId }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "ledger_entries", filter: "workspace_id=eq." + activeWorkspaceId }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "audit_events", filter: "workspace_id=eq." + activeWorkspaceId }, refresh)
      .subscribe((status) => {
        if (status === "SUBSCRIBED") setRealtimeStatus("live")
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") setRealtimeStatus("fallback")
      })
    return () => {
      if (reloadTimer.current !== null) window.clearTimeout(reloadTimer.current)
      reloadTimer.current = null
      window.clearInterval(pollingTimer)
      document.removeEventListener("visibilitychange", refreshWhenVisible)
      void client.removeChannel(channel)
    }
  }, [activeWorkspaceId])

  async function fetchAdminUsers() {
    const api = import.meta.env.VITE_ADMIN_API_URL
    if (!api) return
    setAdminLoading(true)
    const response = await fetch(api + "/admin/users", {
      headers: { Authorization: "Bearer " + session.access_token },
    })
    const body = await response.json() as { users?: AdminUser[]; error?: string }
    setAdminLoading(false)
    if (!response.ok) {
      setError(body.error || "The admin directory could not be loaded.")
      return
    }
    setAdminUsers(body.users || [])
  }

  useEffect(() => {
    if (data?.profile.role === "admin") void fetchAdminUsers()
  }, [data?.profile.role])

  async function adminRequest<T = void>(path: string, body?: unknown): Promise<T> {
    const api = import.meta.env.VITE_ADMIN_API_URL
    if (!api) throw new Error("Set VITE_ADMIN_API_URL before using administrator actions.")
    const response = await fetch(api + path, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + session.access_token,
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
    })
    const result = await response.json() as { error?: string }
    if (!response.ok) throw new Error(result.error || "Administrator action failed.")
    await fetchAdminUsers()
    return result as T
  }

  if (error && !data) return <FatalScreen error={error} />
  if (!data) return <LoadingScreen label="Loading your workspace" fullScreen />

  const admin: AdminControl | undefined = data.profile.role === "admin"
    ? {
        users: adminUsers,
        loading: adminLoading,
        switchWorkspace: setActiveWorkspaceId,
        inviteUser: (email, name) => adminRequest<AdminCredentials>("/admin/users/invite", { email, name }),
        disableUser: (userId) => adminRequest("/admin/users/" + userId + "/disable"),
        resetCredentials: (userId) => adminRequest<AdminCredentials>("/admin/users/" + userId + "/reset-credentials"),
      }
    : undefined

  return <LedgerShell data={data} ownWorkspaceId={ownWorkspaceId} isDemo={false} reload={reload} error={error} admin={admin} realtimeStatus={realtimeStatus} />
}

function LedgerShell({
  data,
  ownWorkspaceId,
  isDemo,
  reload,
  error,
  admin,
  realtimeStatus,
}: {
  data: LedgerData
  ownWorkspaceId: string
  isDemo: boolean
  reload: () => Promise<void>
  error?: string
  admin?: AdminControl
  realtimeStatus?: "connecting" | "live" | "fallback"
}) {
  const [view, setView] = useState<View>("dashboard")
  const [notice, setNotice] = useState(isDemo ? "Demo data is shown. Connect Supabase to save a ledger." : "")
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<Toast | null>(null)
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false)
  const isViewingOther = Boolean(admin && ownWorkspaceId && ownWorkspaceId !== data.workspace.id)

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 2800)
    return () => window.clearTimeout(timer)
  }, [toast])

  function selectView(nextView: View) {
    setView(nextView)
    setMobileMoreOpen(false)
  }

  async function run(action: () => Promise<unknown>, success: string): Promise<boolean> {
    if (isDemo) {
      setNotice("Demo mode is read-only. Connect Supabase to save this action.")
      return false
    }
    setBusy(true)
    setNotice("")
    setToast(null)
    try {
      await action()
      await reload()
      setToast({ kind: "success", message: success })
      return true
    } catch (reason) {
      setToast({ kind: "error", message: reason instanceof Error ? reason.message : "This action could not be completed." })
      return false
    } finally {
      setBusy(false)
    }
  }

  async function signOut() {
    if (isDemo || !supabase) return
    await supabase.auth.signOut()
  }

  return (
    <div className="app-frame">
      <aside className="sidebar">
        <div className="brand-row"><div className="brand-icon"><img src="/icons/icon-192.png" alt="" /></div><span>Action Tracker</span></div>
        <nav aria-label="Primary navigation">
          {nav.map((item) => <NavButton key={item.view} item={item} selected={view === item.view} onClick={() => selectView(item.view)} />)}
          {admin && <NavButton item={{ view: "admin", label: "Admin", icon: ShieldCheck }} selected={view === "admin"} onClick={() => selectView("admin")} />}
        </nav>
        <button className="account-button" onClick={signOut}><span className="avatar">{data.profile.display_name?.slice(0, 1).toUpperCase() || "U"}</span><span><strong>{data.profile.display_name || data.profile.email}</strong><small>{data.profile.role === "admin" ? "Administrator" : "Personal workspace"}</small></span><SignOut size={18} /></button>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <p className="crumb">{isViewingOther ? "Admin workspace" : "Personal workspace"}</p>
            <h1>{titleFor(view)}</h1>
          </div>
          <div className="topbar-actions">
            {admin && <WorkspaceSelect data={data} ownWorkspaceId={ownWorkspaceId} admin={admin} />}
            {!isDemo && realtimeStatus && <span className={"sync-status " + realtimeStatus} title={realtimeStatus === "live" ? "Realtime updates are connected" : realtimeStatus === "fallback" ? "Realtime is reconnecting; foreground refresh is active" : "Connecting to realtime updates"}><span aria-hidden="true" />{realtimeStatus === "live" ? "Live" : realtimeStatus === "fallback" ? "Syncing" : "Connecting"}</span>}
            <button className="primary-button compact" onClick={() => selectView("host")}><Plus size={18} />New bet</button>
          </div>
        </header>

        {isViewingOther && (
          <div className="workspace-banner">
            <ShieldCheck size={20} weight="fill" />
            <span>Viewing <strong>{data.workspace.name}</strong> as administrator. Every action is recorded.</span>
            <button onClick={() => admin?.switchWorkspace(ownWorkspaceId)}>Return to My Ledger</button>
          </div>
        )}
        {isDemo && <div className="setup-banner"><WarningCircle size={19} /><span>{notice}</span></div>}
        {!isDemo && error && <div className="error-banner"><WarningCircle size={19} /><span>{error}</span></div>}
        {notice && !isDemo && <div className={notice.includes("could not") || notice.includes("Set ") ? "error-banner" : "notice-banner"}>{busy ? <SpinnerGap className="spin" size={18} /> : <CheckCircle size={18} />}<span>{notice}</span></div>}

        <section className="page-content">
          {view === "dashboard" && <Dashboard data={data} setView={setView} />}
          {view === "host" && <HostedBetForm data={data} run={run} busy={busy} />}
          {view === "split" && <SplitBetForm data={data} run={run} busy={busy} />}
          {view === "pending" && <PendingBets data={data} run={run} busy={busy} />}
          {view === "adjust" && <Payments data={data} run={run} busy={busy} />}
          {view === "tabs" && <Tabs data={data} run={run} busy={busy} />}
          {view === "history" && <History data={data} run={run} busy={busy} />}
          {view === "partners" && <Partners data={data} run={run} busy={busy} isDemo={isDemo} />}
          {view === "settings" && <Settings data={data} run={run} busy={busy} isDemo={isDemo} />}
          {view === "admin" && admin && <AdminPanel data={data} admin={admin} run={run} busy={busy} />}
        </section>
      </main>

      {toast && <aside className={"toast toast-" + toast.kind} role={toast.kind === "error" ? "alert" : "status"} aria-live="assertive"><span className="toast-icon">{toast.kind === "success" ? <CheckCircle size={27} weight="fill" /> : <XCircle size={27} weight="fill" />}</span><span>{toast.message}</span><button className="icon-button" onClick={() => setToast(null)} aria-label="Dismiss notification"><X size={17} /></button></aside>}

      {mobileMoreOpen && (
        <div className="mobile-more-panel" role="dialog" aria-label="All sections">
          <div className="mobile-more-heading"><strong>All sections</strong><button className="icon-button" onClick={() => setMobileMoreOpen(false)} aria-label="Close sections"><X size={18} /></button></div>
          <div className="mobile-more-grid">
            {nav.map((item) => {
              const Icon = item.icon
              return <button key={item.view} className={view === item.view ? "active" : ""} onClick={() => selectView(item.view)}><Icon size={19} weight={view === item.view ? "fill" : "regular"} /><span>{item.label}</span></button>
            })}
            {admin && <button className={view === "admin" ? "active" : ""} onClick={() => selectView("admin")}><ShieldCheck size={19} weight={view === "admin" ? "fill" : "regular"} /><span>Admin</span></button>}
          </div>
        </div>
      )}

      <nav className="mobile-nav" aria-label="Mobile navigation">
        <button className={view === "dashboard" ? "active" : ""} onClick={() => selectView("dashboard")}><House size={20} /><span>Home</span></button>
        <button className={view === "host" ? "active" : ""} onClick={() => selectView("host")}><Plus size={20} /><span>New</span></button>
        <button className={view === "split" ? "active" : ""} onClick={() => selectView("split")}><ArrowDownLeft size={20} /><span>Split In</span></button>
        <button className={view === "pending" ? "active" : ""} onClick={() => selectView("pending")}><Lightning size={20} /><span>Pending</span></button>
        <button className={view === "tabs" ? "active" : ""} onClick={() => selectView("tabs")}><CurrencyCircleDollar size={20} /><span>Tabs</span></button>
        <button className={view === "history" ? "active" : ""} onClick={() => selectView("history")}><ListBullets size={20} /><span>History</span></button>
        <button className={mobileMoreOpen || !["dashboard", "host", "split", "pending", "tabs", "history"].includes(view) ? "active" : ""} onClick={() => setMobileMoreOpen((open) => !open)}><GearSix size={20} /><span>More</span></button>
      </nav>
    </div>
  )
}

function NavButton({ item, selected, onClick }: { item: { view: View; label: string; icon: typeof House }; selected: boolean; onClick: () => void }) {
  const Icon = item.icon
  return <button className={"nav-button" + (selected ? " selected" : "")} onClick={onClick}><Icon size={20} weight={selected ? "fill" : "regular"} />{item.label}</button>
}

function WorkspaceSelect({ data, ownWorkspaceId, admin }: { data: LedgerData; ownWorkspaceId: string; admin: AdminControl }) {
  const workspaces = admin.users.flatMap((user) => {
    const record = Array.isArray(user.workspaces) ? user.workspaces[0] : user.workspaces
    return record ? [{ id: record.id, name: record.name, disabled: Boolean(user.disabled_at) }] : []
  })
  return (
    <label className="workspace-select">
      <span className="sr-only">Workspace</span>
      <select value={data.workspace.id} onChange={(event) => admin.switchWorkspace(event.target.value)}>
        <option value={ownWorkspaceId}>My Ledger</option>
        {workspaces.filter((workspace) => workspace.id !== ownWorkspaceId).map((workspace) => <option key={workspace.id} value={workspace.id} disabled={workspace.disabled}>{workspace.name}{workspace.disabled ? " (disabled)" : ""}</option>)}
      </select>
      <CaretDown size={16} />
    </label>
  )
}

function Dashboard({ data, setView }: { data: LedgerData; setView: (view: View) => void }) {
  const balancesByUnit = useMemo(() => {
    const grouped = new Map<string, Balance[]>()
    data.balances.forEach((balance) => grouped.set(balance.unit_code, [...(grouped.get(balance.unit_code) || []), balance]))
    return grouped
  }, [data.balances])
  const pending = data.bets.filter((bet) => bet.status === "pending")
  return (
    <div className="dashboard-layout">
      <section className="ledger-overview">
        <section className="owner-total-panel">
          <div><h2>My totals</h2><p>Settled owner results, kept separate by unit.</p></div>
          {data.ownerTotals.length ? <div className="owner-total-list">{data.ownerTotals.map((total) => <div key={total.unit_code}><span>{total.unit_symbol} {total.unit_label}</span><strong>{formatAmount(total.total, total.unit_symbol)}</strong></div>)}</div> : <p className="empty-copy">No settled owner result yet.</p>}
        </section>
        <div className="section-heading">
          <div><h2>What is outstanding</h2><p>Every unit is independent. No balances are converted or combined.</p></div>
          <button className="text-button" onClick={() => setView("tabs")}>Open partner tabs <ArrowCounterClockwise size={16} /></button>
        </div>
        {balancesByUnit.size ? (
          <div className="unit-stack">
            {Array.from(balancesByUnit.entries()).map(([code, balances]) => <UnitBalance key={code} balances={balances} />)}
          </div>
        ) : <EmptyState icon={<CurrencyCircleDollar size={28} />} title="No settlement balance yet" body="Settled bets and recorded payments will appear here by unit." action="Record a payment" onAction={() => setView("adjust")} />}
      </section>
      <aside className="dashboard-side">
        <section className="quiet-panel">
          <div className="section-heading compact-heading"><div><h2>Pending action</h2><p>{pending.length ? "Settle only when the result is confirmed." : "Nothing needs a result."}</p></div><span className="count">{pending.length}</span></div>
          {pending.slice(0, 3).map((bet) => <BetLine key={bet.id} bet={bet} unit={unitFor(data.units, bet.unit_code)} />)}
          {pending.length > 3 && <button className="text-button" onClick={() => setView("pending")}>View all pending bets</button>}
          {pending.length === 0 && <p className="empty-copy">New entries stay pending until you explicitly record Won, Lost, or Void.</p>}
        </section>
        <section className="quiet-panel activity-panel">
          <div className="section-heading compact-heading"><div><h2>Latest activity</h2><p>Append-only ledger entries.</p></div></div>
          {data.entries.slice(0, 4).map((entry) => <ActivityLine key={entry.id} entry={entry} unit={unitFor(data.units, entry.unit_code)} partner={partnerName(data.partners, entry.partner_id)} />)}
        </section>
      </aside>
    </div>
  )
}

function UnitBalance({ balances }: { balances: Balance[] }) {
  const first = balances[0]
  return (
    <section className="unit-group">
      <header><span className="unit-symbol">{first.unit_symbol}</span><div><h3>{first.unit_label}</h3><p>Settle only within this unit</p></div></header>
      <div className="balance-list">
        {balances.map((balance) => {
          const amount = signedAmount(balance.balance)
          return <div className="balance-row" key={balance.partner_id}>
            <span className="partner-dot">{balance.partner_name.slice(0, 1).toUpperCase()}</span>
            <strong>{balance.partner_name}</strong>
            <span className={amount.negative ? "balance pay" : "balance collect"}>{amount.negative ? "Pay" : "Collect"} {formatAmount(balance.balance, balance.unit_symbol)}</span>
          </div>
        })}
      </div>
    </section>
  )
}

function HostedBetForm({ data, run, busy }: { data: LedgerData; run: RunAction; busy: boolean }) {
  const [amount, setAmount] = useState("")
  const [unit, setUnit] = useState(data.units[0]?.code || "")
  const [odds, setOdds] = useState("")
  const [description, setDescription] = useState("")
  const [sourceUrl, setSourceUrl] = useState("")
  const [result, setResult] = useState<Bet["status"]>("pending")
  const [allocations, setAllocations] = useState<Array<{ partnerId: string; amount: string }>>([])

  function addAllocation() {
    const available = data.partners.find((partner) => !partner.archived_at && !allocations.some((item) => item.partnerId === partner.id))
    if (available) setAllocations([...allocations, { partnerId: available.id, amount: "" }])
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (await run(async () => {
      await callLedgerRpc("create_hosted_bet", {
        target_workspace: data.workspace.id,
        input_total: amount,
        input_unit_code: unit,
        input_odds: odds,
        input_description: description,
        input_source_url: sourceUrl,
        input_allocations: allocations.filter((item) => item.partnerId && item.amount).map((item) => ({ partner_id: item.partnerId, amount: item.amount })),
        input_initial_status: result,
      })
    }, "Hosted bet recorded as " + statusLabel(result) + ".")) {
      setAmount("")
      setUnit(data.units[0]?.code || "")
      setOdds("")
      setDescription("")
      setSourceUrl("")
      setResult("pending")
      setAllocations([])
    }
  }

  return <section className="form-panel"><FormHeading title="Host an action" body="Your remaining share is derived on the server from total amount minus partner allocations." /><form className="entry-form" onSubmit={submit}>
    <div className="form-grid"><Field label="Total amount"><input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0.00" required /></Field><UnitField units={data.units} value={unit} onChange={setUnit} /><Field label="Odds"><input inputMode="decimal" value={odds} onChange={(event) => setOdds(event.target.value)} placeholder="2.50" required /></Field><ResultField value={result} onChange={setResult} /></div>
    <Field label="Description"><input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What is this action?" required /></Field>
    <Field label="Source URL"><input type="url" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} placeholder="Discord, receipt, or reference link" /></Field>
    <div className="allocation-section"><div className="inline-heading"><div><h3>Partner allocations</h3><p>Optional. Each amount is assigned exactly once.</p></div><button type="button" className="secondary-button" onClick={addAllocation} disabled={allocations.length >= data.partners.filter((partner) => !partner.archived_at).length}><Plus size={16} />Add partner</button></div>
      {allocations.map((allocation, index) => <div className="allocation-row" key={index}><select value={allocation.partnerId} onChange={(event) => setAllocations(allocations.map((item, itemIndex) => itemIndex === index ? { ...item, partnerId: event.target.value } : item))}>{data.partners.filter((partner) => !partner.archived_at).map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select><input inputMode="decimal" value={allocation.amount} onChange={(event) => setAllocations(allocations.map((item, itemIndex) => itemIndex === index ? { ...item, amount: event.target.value } : item))} placeholder="Allocation" /><button type="button" className="icon-button danger" aria-label="Remove allocation" onClick={() => setAllocations(allocations.filter((_item, itemIndex) => itemIndex !== index))}><XCircle size={20} /></button></div>)}
    </div>
    <button className="primary-button" disabled={busy}>{busy ? <SpinnerGap className="spin" size={18} /> : <ArrowUpRight size={18} />}Record hosted bet</button>
  </form></section>
}

function SplitBetForm({ data, run, busy }: { data: LedgerData; run: RunAction; busy: boolean }) {
  const activePartners = data.partners.filter((partner) => !partner.archived_at)
  const [partnerId, setPartnerId] = useState(activePartners[0]?.id || "")
  const [amount, setAmount] = useState("")
  const [unit, setUnit] = useState(data.units[0]?.code || "")
  const [odds, setOdds] = useState("")
  const [description, setDescription] = useState("")
  const [sourceUrl, setSourceUrl] = useState("")
  const [result, setResult] = useState<Bet["status"]>("pending")
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (await run(async () => {
      await callLedgerRpc("create_split_in_bet", {
        target_workspace: data.workspace.id,
        input_partner: partnerId,
        input_my_share: amount,
        input_unit_code: unit,
        input_odds: odds,
        input_description: description,
        input_source_url: sourceUrl,
        input_initial_status: result,
      })
    }, "Split-in position recorded as " + statusLabel(result) + ".")) {
      setPartnerId(activePartners[0]?.id || "")
      setAmount("")
      setUnit(data.units[0]?.code || "")
      setOdds("")
      setDescription("")
      setSourceUrl("")
      setResult("pending")
    }
  }
  return <section className="form-panel"><FormHeading title="Split in" body="Record your own share with one partner. Settlement follows the legacy contract exactly." /><form className="entry-form" onSubmit={submit}>
    <div className="form-grid"><Field label="Partner"><select value={partnerId} onChange={(event) => setPartnerId(event.target.value)} required>{activePartners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select></Field><Field label="My share"><input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0.00" required /></Field><UnitField units={data.units} value={unit} onChange={setUnit} /><Field label="Odds"><input inputMode="decimal" value={odds} onChange={(event) => setOdds(event.target.value)} placeholder="2.50" required /></Field><ResultField value={result} onChange={setResult} /></div>
    <Field label="Description"><input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What is this split?" required /></Field><Field label="Source URL"><input type="url" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} placeholder="Discord, receipt, or reference link" /></Field>
    <button className="primary-button" disabled={busy || !partnerId}>{busy ? <SpinnerGap className="spin" size={18} /> : <ArrowDownLeft size={18} />}Record split-in position</button>
  </form></section>
}

function PendingBets({ data, run, busy }: { data: LedgerData; run: RunAction; busy: boolean }) {
  const pending = data.bets.filter((bet) => bet.status === "pending")
  const hosted = pending.filter((bet) => bet.kind === "hosted")
  const splitIns = pending.filter((bet) => bet.kind === "split_in")
  function settle(bet: Bet, outcome: "won" | "lost") {
    if (!window.confirm("Record " + statusLabel(outcome) + "? This creates permanent ledger entries.")) return
    void run(() => callLedgerRpc("settle_bet", { target_workspace: data.workspace.id, target_bet: bet.id, input_outcome: outcome }), "Outcome settled and ledger entries recorded.")
  }
  function voidBet(bet: Bet) {
    if (!window.confirm("Void this bet? A settled bet is reversed with new ledger entries.")) return
    void run(() => callLedgerRpc("void_bet", { target_workspace: data.workspace.id, target_bet: bet.id }), "Bet voided. Any prior settlement was reversed.")
  }
  function group(title: string, bets: Bet[]) {
    if (!bets.length) return null
    return <div className="pending-group"><div className="pending-group-heading"><h3>{title}</h3><span>{bets.length}</span></div><div className="bet-list">{bets.map((bet) => <article className="bet-card" key={bet.id}><BetLine bet={bet} unit={unitFor(data.units, bet.unit_code)} /><div className="bet-actions"><button className="outcome win" disabled={busy} onClick={() => settle(bet, "won")}><CheckCircle size={17} />Won</button><button className="outcome loss" disabled={busy} onClick={() => settle(bet, "lost")}><XCircle size={17} />Lost</button><button className="outcome void" disabled={busy} onClick={() => voidBet(bet)}>Void</button></div></article>)}</div></div>
  }
  return <section className="list-panel"><FormHeading title="Pending actions" body="Only Won and Lost change balances. Void is always neutral and reverses an earlier settlement exactly." />
    {pending.length ? <div className="pending-groups">{group("Hosted bets", hosted)}{group("Split-in bets", splitIns)}</div> : <EmptyState icon={<Lightning size={28} />} title="No pending actions" body="A newly recorded hosted or split-in bet will wait here for its confirmed result." />}
  </section>
}

function Payments({ data, run, busy }: { data: LedgerData; run: RunAction; busy: boolean }) {
  const activePartners = data.partners.filter((partner) => !partner.archived_at)
  const [partnerId, setPartnerId] = useState(activePartners[0]?.id || "")
  const [unit, setUnit] = useState(data.units[0]?.code || "")
  const [settlementAction, setSettlementAction] = useState("collected")
  const [amount, setAmount] = useState("")
  const [proofUrl, setProofUrl] = useState("")
  const [notes, setNotes] = useState("")

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (await run(() => callLedgerRpc("record_settlement", {
      target_workspace: data.workspace.id,
      target_partner: partnerId,
      input_unit_code: unit,
      input_action: settlementAction,
      input_amount: amount,
      input_proof_url: proofUrl,
      input_notes: notes,
    }), "Payment recorded permanently.")) {
      setPartnerId(activePartners[0]?.id || "")
      setUnit(data.units[0]?.code || "")
      setSettlementAction("collected")
      setAmount("")
      setProofUrl("")
      setNotes("")
    }
  }

  return <section className="form-panel"><FormHeading title="Record payment" body="Log money that was actually collected from or paid to a partner. Payments appear in the activity log." /><form className="entry-form" onSubmit={submit}><PartnerAndUnit partners={activePartners} partnerId={partnerId} setPartnerId={setPartnerId} units={data.units} unit={unit} setUnit={setUnit} /><Field label="Payment direction"><select value={settlementAction} onChange={(event) => setSettlementAction(event.target.value)}><option value="collected">Collected from partner</option><option value="paid">Paid to partner</option></select></Field><Field label="Amount"><input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0.00" required /></Field><Field label="Proof URL"><input type="url" value={proofUrl} onChange={(event) => setProofUrl(event.target.value)} placeholder="Optional receipt or payment proof" /></Field><Field label="Notes"><input value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional context" /></Field><button className="secondary-button full-width" disabled={busy || !partnerId}><CheckCircle size={18} />Record payment</button></form></section>
}

function Tabs({ data, run, busy }: { data: LedgerData; run: RunAction; busy: boolean }) {
  const unitName = new Map(data.units.map((unit) => [unit.code, unit]))
  const activePartners = data.partners.filter((partner) => !partner.archived_at)
  const [transferTargets, setTransferTargets] = useState<Record<string, string>>({})
  const [copiedLinksFor, setCopiedLinksFor] = useState("")
  const [downloadedHistoryFor, setDownloadedHistoryFor] = useState("")
  const byPartner = new Map<string, Balance[]>()
  data.balances.forEach((balance) => byPartner.set(balance.partner_id, [...(byPartner.get(balance.partner_id) || []), balance]))
  const pendingByPartner = new Map<string, number>()
  data.bets.filter((bet) => bet.status === "pending").forEach((bet) => {
    const partnerIds = bet.kind === "split_in" ? [bet.split_partner_id] : bet.allocations?.map((allocation) => allocation.partner_id)
    partnerIds?.forEach((partnerId) => {
      if (partnerId) pendingByPartner.set(partnerId, (pendingByPartner.get(partnerId) || 0) + 1)
    })
  })
  const pendingSplitInsByPartner = new Map<string, Bet[]>()
  data.bets.forEach((bet) => {
    if (bet.kind !== "split_in" || bet.status !== "pending" || !bet.split_partner_id || !bet.source_url) return
    pendingSplitInsByPartner.set(bet.split_partner_id, [...(pendingSplitInsByPartner.get(bet.split_partner_id) || []), bet])
  })
  const displayPartners = data.partners.filter((partner) => !partner.archived_at || byPartner.has(partner.id))
  function targetFor(sourceId: string) {
    const available = activePartners.filter((partner) => partner.id !== sourceId)
    return available.some((partner) => partner.id === transferTargets[sourceId]) ? transferTargets[sourceId] : available[0]?.id || ""
  }
  function transfer(partner: Partner) {
    const targetId = targetFor(partner.id)
    const target = activePartners.find((candidate) => candidate.id === targetId)
    if (!target || !window.confirm(`Move every outstanding balance from ${partner.name} to ${target.name}? ${partner.name} will be zeroed in each unit.`)) return
    void run(() => callLedgerRpc("transfer_partner_balance", {
      target_workspace: data.workspace.id,
      source_partner: partner.id,
      destination_partner: target.id,
    }), `Moved ${partner.name}'s outstanding balance to ${target.name}.`)
  }
  async function copyPendingLinks(partnerId: string, bets: Bet[]) {
    const links = bets.flatMap((bet) => bet.source_url ? [bet.source_url] : [])
    if (!links.length) return
    try {
      await navigator.clipboard.writeText(links.join("\n"))
      setCopiedLinksFor(partnerId)
    } catch {
      window.prompt("Copy pending links", links.join("\n"))
    }
  }
  function partnerHistory(partnerId: string) {
    return data.bets.flatMap((bet) => bet.kind === "hosted"
      ? (bet.allocations || []).filter((allocation) => allocation.partner_id === partnerId).map((allocation) => ({ bet, amount: allocation.amount, type: "Hosted" }))
      : bet.split_partner_id === partnerId ? [{ bet, amount: bet.total_amount, type: "Split in" }] : [])
  }
  async function downloadPartnerHistory(partner: Partner) {
    const rows = partnerHistory(partner.id)
    if (!rows.length) return
    const cell = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`
    const csv = [["Date", "Bet type", "Result", "Partner amount", "Unit", "Odds", "Description", "Source URL"], ...rows.map(({ bet, amount, type }) => [bet.created_at, type, statusLabel(bet.status), amount, unitName.get(bet.unit_code)?.label || bet.unit_code, bet.odds, bet.description, bet.source_url || ""])].map((row) => row.map(cell).join(",")).join("\n")
    const filename = (partner.name.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "partner") + "-bet-history.csv"
    const file = new File([csv], filename, { type: "text/csv;charset=utf-8" })
    const isIosPwa = window.matchMedia("(display-mode: standalone)").matches && (/iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    if (isIosPwa && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: partner.name + " bet history" })
        setDownloadedHistoryFor(partner.id)
        return
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return
      }
    }
    const url = URL.createObjectURL(file)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = filename
    document.body.append(anchor)
    anchor.click()
    anchor.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
    setDownloadedHistoryFor(partner.id)
  }
  return <section className="list-panel"><FormHeading title="Partner tabs" body="Per-unit balances stay separate. Pending actions are visible before they settle." />{displayPartners.length ? <div className="partner-tabs">{displayPartners.map((partner) => { const balances = byPartner.get(partner.id) || []; const pending = pendingByPartner.get(partner.id) || 0; const pendingLinks = pendingSplitInsByPartner.get(partner.id) || []; const history = partnerHistory(partner.id); const targetId = targetFor(partner.id); return <article className="partner-tab" key={partner.id}><div className="tab-name"><span className="partner-dot">{partner.name.slice(0, 1)}</span><h3>{partner.name}</h3></div><div>{balances.length ? balances.map((balance) => { const value = signedAmount(balance.balance); const unit = unitName.get(balance.unit_code); return <p className={value.negative ? "pay" : "collect"} key={balance.unit_code}>{value.negative ? "Pay" : "Collect"} <strong>{formatAmount(balance.balance, unit?.symbol || "")}</strong><small>{unit?.label}</small></p> }) : <p className="tab-empty">No settled balance yet.</p>}{pending > 0 && <p className="tab-pending">{pending} pending {pending === 1 ? "action" : "actions"}</p>}{history.length > 0 && <button className="secondary-button tab-history-download" type="button" onClick={() => void downloadPartnerHistory(partner)}><DownloadSimple size={17} />{downloadedHistoryFor === partner.id ? "Downloaded" : "Download history"}</button>}{pendingLinks.length > 0 && <div className="tab-pending-links"><div><small>Pending split-in links</small><button className="text-button" type="button" onClick={() => void copyPendingLinks(partner.id, pendingLinks)}>{copiedLinksFor === partner.id ? "Copied" : "Copy links"}</button></div><ul>{pendingLinks.map((bet) => <li key={bet.id}><a href={bet.source_url!} target="_blank" rel="noreferrer">{bet.description || "Open bet link"}</a></li>)}</ul></div>}{balances.length > 0 && !partner.archived_at && <div className="tab-transfer"><label>Move all balances to<select value={targetId} onChange={(event) => setTransferTargets({ ...transferTargets, [partner.id]: event.target.value })} disabled={busy || activePartners.length < 2}>{activePartners.filter((candidate) => candidate.id !== partner.id).map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</select></label><button className="secondary-button" type="button" disabled={busy || !targetId} onClick={() => transfer(partner)}>Move balance</button></div>}</div></article> })}</div> : <EmptyState icon={<UsersThree size={28} />} title="No partners yet" body="Add a partner, then their tab and pending actions will appear here." />}</section>
}

function History({ data, run, busy }: { data: LedgerData; run: RunAction; busy: boolean }) {
  const pageSize = 20
  const [auditPage, setAuditPage] = useState(0)
  const pageCount = Math.max(1, Math.ceil(data.auditEvents.length / pageSize))
  const firstAudit = auditPage * pageSize
  const visibleAudits = data.auditEvents.slice(firstAudit, firstAudit + pageSize)
  useEffect(() => setAuditPage((page) => Math.min(page, pageCount - 1)), [pageCount])

  function voidBet(bet: Bet) {
    if (!window.confirm("Delete this bet? It will be marked Void and any settlement will be reversed in the ledger.")) return
    void run(() => callLedgerRpc("void_bet", { target_workspace: data.workspace.id, target_bet: bet.id }), "Bet deleted safely and reversal logged.")
  }
  return <div className="history-layout"><section className="list-panel"><FormHeading title="History" body="Every bet stays visible. Delete safely marks it Void and writes an exact reversal when needed." /><div className="bet-list">{data.bets.map((bet) => <HistoryBet key={bet.id} bet={bet} data={data} run={run} busy={busy} onDelete={() => voidBet(bet)} />)}</div></section><section className="list-panel audit-panel"><FormHeading title="Activity log" body="Creates, edits, settlements, reversals, imports, and workspace actions are recorded here." />{data.auditEvents.length ? <><div className="audit-list">{visibleAudits.map((event) => <div className="audit-row" key={event.id}><div><strong>{auditLabel(event.action)}</strong><small>{auditDetails(event.metadata)}</small></div><time dateTime={event.created_at}>{new Date(event.created_at).toLocaleString()}</time></div>)}</div><div className="audit-pagination"><span>Showing {firstAudit + 1}–{Math.min(firstAudit + pageSize, data.auditEvents.length)} of {data.auditEvents.length}</span><div><button className="text-button" disabled={auditPage === 0} onClick={() => setAuditPage((page) => page - 1)}>Previous</button><button className="text-button" disabled={auditPage >= pageCount - 1} onClick={() => setAuditPage((page) => page + 1)}>Next</button></div></div></> : <p className="empty-copy">No activity has been logged yet.</p>}</section></div>
}

function HistoryBet({ bet, data, run, busy, onDelete }: { bet: Bet; data: LedgerData; run: RunAction; busy: boolean; onDelete: () => void }) {
  const [editing, setEditing] = useState(false)
  return <article className="bet-card"><BetLine bet={bet} unit={unitFor(data.units, bet.unit_code)} /><BetPartners bet={bet} data={data} /><div className="history-actions">{bet.status === "pending" && <button className="text-button" disabled={busy} onClick={() => setEditing((open) => !open)}>{editing ? "Close edit" : "Edit"}</button>}{bet.status !== "void" && <button className="text-button danger-text" disabled={busy} onClick={onDelete}>Delete safely</button>}</div>{editing && <EditBetForm bet={bet} data={data} run={run} busy={busy} onDone={() => setEditing(false)} />}</article>
}

function BetPartners({ bet, data }: { bet: Bet; data: LedgerData }) {
  const allocations = bet.kind === "hosted" ? bet.allocations || [] : bet.split_partner_id ? [{ partner_id: bet.split_partner_id, amount: bet.total_amount }] : []
  if (!allocations.length) return null
  const names = new Map(data.partners.map((partner) => [partner.id, partner.name]))
  const unit = unitFor(data.units, bet.unit_code)
  return <div className="bet-partners"><small>{bet.kind === "hosted" ? "Partner allocations" : "Split partner"}</small><div className="bet-partner-list">{allocations.map((allocation) => <div key={allocation.partner_id}><span>{names.get(allocation.partner_id) || "Unknown partner"}</span><strong>{formatAmount(allocation.amount, unit?.symbol || "")}</strong></div>)}</div></div>
}

function EditBetForm({ bet, data, run, busy, onDone }: { bet: Bet; data: LedgerData; run: RunAction; busy: boolean; onDone: () => void }) {
  const activePartners = data.partners.filter((partner) => !partner.archived_at)
  const [amount, setAmount] = useState(bet.total_amount)
  const [partnerId, setPartnerId] = useState(bet.split_partner_id || activePartners[0]?.id || "")
  const [unit, setUnit] = useState(bet.unit_code)
  const [odds, setOdds] = useState(bet.odds)
  const [description, setDescription] = useState(bet.description)
  const [sourceUrl, setSourceUrl] = useState(bet.source_url || "")
  const [allocations, setAllocations] = useState((bet.allocations || []).map((allocation) => ({ partnerId: allocation.partner_id, amount: allocation.amount })))

  function addAllocation() {
    const available = activePartners.find((partner) => !allocations.some((allocation) => allocation.partnerId === partner.id))
    if (available) setAllocations([...allocations, { partnerId: available.id, amount: "" }])
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (await run(() => callLedgerRpc("edit_bet", {
      target_workspace: data.workspace.id,
      target_bet: bet.id,
      input_total: amount,
      input_unit_code: unit,
      input_odds: odds,
      input_description: description,
      input_source_url: sourceUrl,
      input_partner: bet.kind === "split_in" ? partnerId : null,
      input_allocations: bet.kind === "hosted" ? allocations.filter((allocation) => allocation.partnerId && allocation.amount).map((allocation) => ({ partner_id: allocation.partnerId, amount: allocation.amount })) : [],
    }), "Bet changes saved and logged.")) onDone()
  }

  return <form className="edit-bet-form" onSubmit={submit}><div className="form-grid"><Field label={bet.kind === "hosted" ? "Total amount" : "My share"}><input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} required /></Field>{bet.kind === "split_in" && <Field label="Partner"><select value={partnerId} onChange={(event) => setPartnerId(event.target.value)} required>{activePartners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select></Field>}<UnitField units={data.units} value={unit} onChange={setUnit} /><Field label="Odds"><input inputMode="decimal" value={odds} onChange={(event) => setOdds(event.target.value)} required /></Field></div><Field label="Description"><input value={description} onChange={(event) => setDescription(event.target.value)} required /></Field><Field label="Source URL"><input type="url" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} /></Field>{bet.kind === "hosted" && <div className="allocation-section"><div className="inline-heading"><div><h3>Partner allocations</h3><p>Amounts are recalculated server-side.</p></div><button type="button" className="secondary-button" onClick={addAllocation} disabled={allocations.length >= activePartners.length}><Plus size={16} />Add partner</button></div>{allocations.map((allocation, index) => <div className="allocation-row" key={index}><select value={allocation.partnerId} onChange={(event) => setAllocations(allocations.map((item, itemIndex) => itemIndex === index ? { ...item, partnerId: event.target.value } : item))}>{activePartners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select><input inputMode="decimal" value={allocation.amount} onChange={(event) => setAllocations(allocations.map((item, itemIndex) => itemIndex === index ? { ...item, amount: event.target.value } : item))} placeholder="Allocation" /><button type="button" className="icon-button danger" aria-label="Remove allocation" onClick={() => setAllocations(allocations.filter((_item, itemIndex) => itemIndex !== index))}><XCircle size={20} /></button></div>)}</div>}<div className="edit-actions"><button className="primary-button" disabled={busy}><CheckCircle size={18} />Save changes</button><button type="button" className="secondary-button" onClick={onDone}>Cancel</button></div></form>
}

function Partners({ data, run, busy, isDemo }: { data: LedgerData; run: RunAction; busy: boolean; isDemo: boolean }) {
  const [name, setName] = useState("")
  function addPartner(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void run(async () => {
      await callLedgerRpc("create_partner", { target_workspace: data.workspace.id, input_name: name })
    }, "Partner added.")
      .then((success) => { if (success) setName("") })
  }
  function remove(partner: Partner) {
    if (!window.confirm("Remove " + partner.name + "? Partners with history are archived, not deleted.")) return
    void run(() => callLedgerRpc("remove_partner", { target_workspace: data.workspace.id, target_partner: partner.id }), "Partner removed safely.")
  }
  return <section className="list-panel"><FormHeading title="Partners" body="Names belong only to this workspace. Removing one archives any partner with history." /><form className="partner-add" onSubmit={addPartner}><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Partner name" required /><button className="primary-button compact" disabled={busy}><Plus size={17} />Add partner</button></form><div className="partner-directory">{data.partners.map((partner) => <div key={partner.id} className="directory-row"><span className="partner-dot">{partner.name.slice(0, 1).toUpperCase()}</span><strong>{partner.name}</strong><span className={partner.archived_at ? "archived-tag" : "active-tag"}>{partner.archived_at ? "Archived" : "Active"}</span>{!partner.archived_at && <button className="text-button danger-text" onClick={() => remove(partner)} disabled={busy || isDemo}>Remove</button>}</div>)}</div></section>
}

function Settings({ data, run, busy, isDemo }: { data: LedgerData; run: RunAction; busy: boolean; isDemo: boolean }) {
  const [importSummary, setImportSummary] = useState("")
  const [legacyPayload, setLegacyPayload] = useState<unknown>(null)
  const [legacyChecksum, setLegacyChecksum] = useState("")
  const [archivePassword, setArchivePassword] = useState("")
  const [archiveConfirmation, setArchiveConfirmation] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const dataSize = new TextEncoder().encode(JSON.stringify({ bets: data.bets, adjustments: data.adjustments, partners: data.partners })).byteLength
  async function savePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (await run(async () => {
      if (newPassword.length < 8) throw new Error("Use at least 8 characters for your password.")
      if (newPassword !== passwordConfirmation) throw new Error("Passwords do not match.")
      if (!supabase) throw new Error("Supabase is not configured.")
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw new Error(error.message)
    }, "Password saved. You can now sign in with email and password.")) {
      setNewPassword("")
      setPasswordConfirmation("")
    }
  }
  async function inspectImport(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const parsed = JSON.parse(await file.text()) as { bets?: unknown[]; adjustments?: unknown[]; partners?: unknown[] }
      if (!Array.isArray(parsed.bets) || !Array.isArray(parsed.adjustments) || !Array.isArray(parsed.partners)) throw new Error("That is not an Action Tracker v12 backup.")
      const canonical = JSON.stringify(parsed)
      const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical))
      setLegacyChecksum(Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join(""))
      setLegacyPayload(parsed)
      setImportSummary("Validated preview: " + parsed.bets.length + " bets, " + parsed.adjustments.length + " adjustments, " + parsed.partners.length + " partners. Import runs atomically after confirmation.")
    } catch (reason) {
      setLegacyPayload(null)
      setLegacyChecksum("")
      setImportSummary(reason instanceof Error ? reason.message : "That file could not be read.")
    }
  }
  function importLegacy() {
    if (!legacyPayload || !legacyChecksum) return
    if (!window.confirm("Import this legacy backup? It will append an auditable, atomic migration and cannot be run twice.")) return
    void run(() => callLedgerRpc("import_legacy_v12", {
      target_workspace: data.workspace.id,
      legacy_data: legacyPayload,
      input_checksum: legacyChecksum,
    }), "Legacy backup imported atomically.")
  }
  function archive(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void run(async () => {
      if (!supabase) return
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: data.profile.email, password: archivePassword })
      if (signInError) throw new Error("Reauthentication failed. Your workspace was not changed.")
      await downloadSnapshot(data.workspace.id, "action-tracker-" + data.workspace.id + ".json")
      await callLedgerRpc("archive_workspace", { target_workspace: data.workspace.id, input_confirmation: archiveConfirmation })
    }, "Workspace archived after exporting a recovery snapshot.")
  }
  return <section className="settings-layout"><section className="form-panel"><FormHeading title="Account security" body="Set or change the password used to sign in. Your administrator can issue new credentials if needed." /><form className="entry-form security-form" onSubmit={savePassword}><Field label="New password"><input type="password" autoComplete="new-password" minLength={8} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="At least 8 characters" required /></Field><Field label="Confirm password"><input type="password" autoComplete="new-password" minLength={8} value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} placeholder="Repeat your password" required /></Field><button className="primary-button" disabled={busy || isDemo}><LockKey size={18} />Set password</button></form></section><section className="form-panel"><FormHeading title="Data portability" body="Exports are versioned snapshots. Import previews validate legacy v12 files before anything changes." /><div className="settings-stats"><div><small>Total bets</small><strong>{data.bets.length}</strong></div><div><small>Adjustments</small><strong>{data.adjustments.length}</strong></div><div><small>Partners</small><strong>{data.partners.length}</strong></div><div><small>Data size</small><strong>{formatBytes(dataSize)}</strong></div></div><div className="settings-actions"><button className="secondary-button" disabled={isDemo || busy} onClick={() => void downloadSnapshot(data.workspace.id, "action-tracker-" + data.workspace.id + ".json")}><DownloadSimple size={18} />Export workspace</button><label className="secondary-button file-button"><FileArrowUp size={18} />Preview v12 import<input type="file" accept="application/json" onChange={inspectImport} /></label>{legacyPayload !== null && <button className="primary-button compact" disabled={isDemo || busy} onClick={importLegacy}><FileArrowUp size={17} />Import preview</button>}</div>{importSummary && <p className="import-summary">{importSummary}</p>}<p className="settings-note">Google Drive snapshots run daily through the backup worker. Supabase PITR remains the database recovery layer.</p></section>
    <section className="form-panel danger-panel"><FormHeading title="Archive this workspace" body="This replaces destructive reset. It exports first, requires your password, and preserves the audit record." /><form className="entry-form" onSubmit={archive}><Field label="Current password"><input type="password" autoComplete="current-password" value={archivePassword} onChange={(event) => setArchivePassword(event.target.value)} required /></Field><Field label={'Type "ARCHIVE ' + data.workspace.name + '"'}><input value={archiveConfirmation} onChange={(event) => setArchiveConfirmation(event.target.value)} required /></Field><button className="danger-button" disabled={busy || isDemo}><WarningCircle size={18} />Archive workspace</button></form></section></section>
}

function AdminPanel({ data, admin, run, busy }: { data: LedgerData; admin: AdminControl; run: RunAction; busy: boolean }) {
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [credentials, setCredentials] = useState<AdminCredentials | null>(null)
  const [copied, setCopied] = useState(false)

  function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void run(async () => {
      const nextCredentials = await admin.inviteUser(email, name)
      setCredentials(nextCredentials)
      setCopied(false)
      setEmail("")
      setName("")
    }, "User created and credentials generated.")
  }

  function resetCredentials(userId: string) {
    void run(async () => {
      const nextCredentials = await admin.resetCredentials(userId)
      setCredentials(nextCredentials)
      setCopied(false)
    }, "New credentials generated.")
  }

  async function copyCredentials() {
    if (!credentials) return
    try {
      await navigator.clipboard.writeText(credentials.email + ":" + credentials.password)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }
  return <div className="admin-layout"><section className="form-panel"><FormHeading title="Create a user" body="The account and private workspace are created immediately. Share the generated email:password securely." /><form className="entry-form" onSubmit={invite}><Field label="Name"><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Optional display name" /></Field><Field label="Email"><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="person@example.com" required /></Field><button className="primary-button" disabled={busy}><Plus size={18} />Create user</button></form>{credentials && <div className="credential-card" role="status"><div><h3>Share credentials</h3><p>Shown only here. Copy them before dismissing this card.</p></div><code>{credentials.email}:{credentials.password}</code><div className="credential-actions"><button className="primary-button" onClick={() => void copyCredentials()}><DownloadSimple size={18} />{copied ? "Copied" : "Copy email:password"}</button><button className="text-button" onClick={() => setCredentials(null)}>Dismiss</button></div></div>}</section>
    <section className="list-panel"><FormHeading title="Users" body="Open a workspace to review it. Cross-workspace actions show a persistent audited context." />{admin.loading ? <LoadingScreen label="Loading users" /> : <div className="user-list">{admin.users.map((user) => { const workspace = Array.isArray(user.workspaces) ? user.workspaces[0] : user.workspaces; return <div className="user-row" key={user.id}><span className="partner-dot">{(user.display_name || user.email).slice(0, 1).toUpperCase()}</span><div><strong>{user.display_name || user.email}</strong><small>{user.email}{user.role === "admin" ? " · Administrator" : ""}</small></div><span className={user.disabled_at ? "archived-tag" : "active-tag"}>{user.disabled_at ? "Disabled" : "Active"}</span>{workspace && <button className="text-button" onClick={() => admin.switchWorkspace(workspace.id)}>Open ledger</button>}{!user.disabled_at && user.id !== data.profile.id && <><button className="text-button" disabled={busy} onClick={() => resetCredentials(user.id)}>New credentials</button><button className="text-button danger-text" disabled={busy} onClick={() => { if (window.confirm("Disable this user? Their RLS access stops immediately.")) void run(() => admin.disableUser(user.id), "User disabled and access revoked.") }}>Disable</button></>}</div> })}</div>}</section></div>
}

function BetLine({ bet, unit }: { bet: Bet; unit?: Unit }) {
  const statusCopy: Record<Bet["status"], string> = { pending: "Pending", won: "Won", lost: "Lost", void: "Void" }
  return <div className="bet-line"><div className="bet-type"><span className={"status-dot " + bet.status}></span><div><strong>{bet.kind === "hosted" ? "Hosted" : "Split in"} · {statusCopy[bet.status]}</strong><p>{bet.description}</p></div></div><div className="bet-amount"><strong>{formatAmount(bet.total_amount, unit?.symbol || "")}</strong><small>{bet.odds}× · {unit?.label || bet.unit_code}</small></div>{bet.source_url && <a href={bet.source_url} target="_blank" rel="noreferrer">Source</a>}</div>
}

function ActivityLine({ entry, unit, partner }: { entry: LedgerData["entries"][number]; unit?: Unit; partner: string }) {
  const value = signedAmount(entry.amount)
  return <div className="activity-row"><div><strong>{partner}</strong><small>{entry.notes || entry.kind.replaceAll("_", " ")}</small></div><span className={value.negative ? "pay" : "collect"}>{formatAmount(entry.amount, unit?.symbol || "")}</span></div>
}

function FormHeading({ title, body }: { title: string; body: string }) {
  return <div className="form-heading"><h2>{title}</h2><p>{body}</p></div>
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}</label>
}

function UnitField({ units, value, onChange }: { units: Unit[]; value: string; onChange: (value: string) => void }) {
  return <Field label="Settlement unit"><select value={value} onChange={(event) => onChange(event.target.value)}>{units.filter((unit) => !unit.archived_at).map((unit) => <option key={unit.id} value={unit.code}>{unit.symbol} {unit.label}</option>)}</select></Field>
}

function ResultField({ value, onChange }: { value: Bet["status"]; onChange: (value: Bet["status"]) => void }) {
  return <Field label="Result"><select value={value} onChange={(event) => onChange(event.target.value as Bet["status"])}><option value="pending">Pending</option><option value="won">Won</option><option value="lost">Lost</option><option value="void">Void</option></select></Field>
}

function PartnerAndUnit({ partners, partnerId, setPartnerId, units, unit, setUnit }: { partners: Partner[]; partnerId: string; setPartnerId: (value: string) => void; units: Unit[]; unit: string; setUnit: (value: string) => void }) {
  return <div className="form-grid"><Field label="Partner"><select value={partnerId} onChange={(event) => setPartnerId(event.target.value)} required>{partners.map((partner) => <option value={partner.id} key={partner.id}>{partner.name}</option>)}</select></Field><UnitField units={units} value={unit} onChange={setUnit} /></div>
}

function EmptyState({ icon, title, body, action, onAction }: { icon: ReactNode; title: string; body: string; action?: string; onAction?: () => void }) {
  return <div className="empty-state"><span>{icon}</span><h3>{title}</h3><p>{body}</p>{action && <button className="secondary-button" onClick={onAction}>{action}</button>}</div>
}

function LoadingScreen({ label, fullScreen = false }: { label: string; fullScreen?: boolean }) {
  if (!fullScreen) return <div className="loading-screen" role="status" aria-live="polite"><SpinnerGap className="spin" size={24} /><span>{label}</span></div>
  return <main className="loading-page" role="status" aria-live="polite">
    <div className="loading-content">
      <div className="loading-brand"><span className="brand-icon"><img src="/icons/icon-192.png" alt="" /></span><strong>Action Tracker</strong></div>
      <SpinnerGap className="loading-spinner spin" size={28} aria-hidden="true" />
      <p>{label}</p>
    </div>
  </main>
}

function FatalScreen({ error }: { error: string }) {
  return <main className="auth-page"><section className="auth-panel"><WarningCircle size={28} /><h1>We could not open your ledger</h1><p className="auth-intro">{error}</p><button className="primary-button" onClick={() => window.location.reload()}>Try again</button></section></main>
}

function unitFor(units: Unit[], code: string) {
  return units.find((unit) => unit.code === code)
}

function partnerName(partners: Partner[], id: string | null) {
  return partners.find((partner) => partner.id === id)?.name || "Owner"
}

function auditLabel(action: string) {
  return action.replaceAll(".", " · ").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function auditDetails(metadata: Record<string, unknown>) {
  return Object.entries(metadata || {}).filter(([key]) => key !== "previous" && key !== "next").map(([key, value]) => key.replaceAll("_", " ") + ": " + (typeof value === "object" ? JSON.stringify(value) : String(value))).join(" · ") || "Recorded in the append-only audit trail."
}

function statusLabel(status: Bet["status"]) {
  return ({ pending: "Pending", won: "Won", lost: "Lost", void: "Void" } as Record<Bet["status"], string>)[status]
}

function formatBytes(bytes: number) {
  return (bytes / 1024).toFixed(1) + " KB"
}

function titleFor(view: View) {
  return ({ dashboard: "Dashboard", host: "Host an action", split: "Split in", pending: "Pending", adjust: "Record payment", tabs: "Partner tabs", history: "History", partners: "Partners", settings: "Settings", admin: "Admin" } as Record<View, string>)[view]
}
